import { convertDegreesToRadians, convertRadiansToDegrees } from '../../common/helpers/utils';
import type { GeoPoint, GeoPointOffset } from '../../location/types';

function haversineOffset(point: GeoPoint, offset: GeoPointOffset): GeoPoint {
    const R = 6378137;
    const aLat = point.latitude;
    const aLng = point.longitude;
    const x = offset.x;
    const y = offset.y;

    const dLat = y / R;
    const dLng = x / (R * Math.cos(convertDegreesToRadians(aLat)));

    return {
        latitude: aLat + convertRadiansToDegrees(dLat),
        longitude: aLng + convertRadiansToDegrees(dLng),
    };
}

/**
 * 1. Figure out how much each offset step is based on the zoom level
 * 2. Keep a running tally of *exact* lat/lng matches
 * 3. Based on the number of previously seen matches, calculate
 *    how many x/y steps to offset the marker, following the pattern:
 *        2
 *      3 0 1
 *        4
 * 4. Use the haversine offset formula and the offset step to convert to a new lat/lng
 */
export function initOffsetCalculator(zoom: number) {
    let offsetStep: number;
    // Roughly powers of 2 increase with each zoom level, with some adjustments for
    // changing icon sizes
    switch (zoom) {
        case 20:
            offsetStep = 3;
            break;
        case 19:
            offsetStep = 6;
            break;
        case 18:
            offsetStep = 12;
            break;
        case 17:
            offsetStep = 24;
            break;
        case 16:
            offsetStep = 32;
            break;
        case 15:
            offsetStep = 64;
            break;
        case 14:
            offsetStep = 90;
            break;
        case 13:
            offsetStep = 180;
            break;
        default:
            offsetStep = 0;
    }
    const latLngMap: Record<string, number[]> = {};
    return (
        latBase: number | string,
        lngBase: number | string,
        eventId: number,
        isEventSelected: boolean
    ) => {
        const lat = typeof latBase === 'number' ? latBase : parseInt(latBase);
        const lng = typeof lngBase === 'number' ? lngBase : parseInt(lngBase);
        // use location to 5 decimal places
        const key = `${Math.round(lat * 10000)}_${Math.round(lng * 10000)}`;
        if (latLngMap[key]?.length && !latLngMap[key].includes(eventId)) {
            latLngMap[key].push(eventId);
        } else {
            latLngMap[key] = [eventId];
        }
        const index = latLngMap[key].length - 1;
        let eventIdToReplaceGeoLocation;
        let xOffset;

        switch (index % 4) {
            case 1:
                xOffset = Math.ceil(index / 4);
                break;
            case 3:
                xOffset = Math.ceil(index / 4) * -1;
                break;
            case 0:
            case 2:
            default:
                xOffset = 0;
        }
        let yOffset;
        switch (index % 4) {
            case 2:
                yOffset = Math.ceil(index / 4);
                break;
            case 0:
                yOffset = (index / 4) * -1;
                break;
            case 1:
            case 3:
            default:
                yOffset = 0;
        }

        if (isEventSelected && latLngMap[key].length > 1) {
            eventIdToReplaceGeoLocation = latLngMap[key][0];
        }

        return {
            eventIdToReplaceGeoLocation,
            adjustedLatLng: haversineOffset(
                { latitude: lat, longitude: lng },
                { x: offsetStep * xOffset, y: offsetStep * yOffset }
            ),
        };
    };
}
